import React, { useState, useRef, useEffect } from 'react';
import { Settings, Menu, X, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ThemeSwitcher from './ThemeSwitcher';
import Logo from './Logo';

export default function Header({ user, userNickname, onSignOut, onLogin, onSignUp, onNavigate, onNavigateToModule, currentView, currentLang, onLangChange, streak, isDarkMode, onToggleDark, onOpenSettings, currentTheme, onThemeChange }) {
  const [showLangMenu, setShowLangMenu]       = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [drawerOpen, setDrawerOpen]           = useState(false);
  const [activeNavMenu, setActiveNavMenu]     = useState(null); // 'study' | 'explore' | 'info'

  const langRef    = useRef(null);
  const profileRef = useRef(null);
  const drawerRef  = useRef(null);
  const navRef     = useRef(null);
  const routerNav  = useNavigate();

  useEffect(() => {
    function handleClickOutside(event) {
      // Don't close desktop dropdowns when clicking inside the mobile drawer
      const inDrawer = drawerRef.current && drawerRef.current.contains(event.target);
      if (!inDrawer) {
        if (langRef.current    && !langRef.current.contains(event.target))    setShowLangMenu(false);
        if (profileRef.current && !profileRef.current.contains(event.target)) setShowProfileMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close nav dropdowns on outside click
  useEffect(() => {
    function handleNavOutside(event) {
      if (navRef.current && !navRef.current.contains(event.target)) setActiveNavMenu(null);
    }
    document.addEventListener('mousedown', handleNavOutside);
    return () => document.removeEventListener('mousedown', handleNavOutside);
  }, []);

  // Close drawer on ESC
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setDrawerOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  // Lock body scroll when drawer open
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  const languageGroups = [
    {
      label: 'Western',
      langs: [
        { code: 'en',  label: 'English',   flag: '🇬🇧' },
        { code: 'es',  label: 'Español',   flag: '🇪🇸' },
        { code: 'fr',  label: 'Français',  flag: '🇫🇷' },
        { code: 'de',  label: 'Deutsch',   flag: '🇩🇪' },
        { code: 'it',  label: 'Italiano',  flag: '🇮🇹' },
      ],
    },
    {
      label: 'Eastern',
      langs: [
        { code: 'zh',    label: '简体中文',    flag: '🇨🇳' },
        { code: 'ko',    label: '한국어',      flag: '🇰🇷' },
        { code: 'vi',    label: 'Tiếng Việt', flag: '🇻🇳' },
        { code: 'zh-TW', label: '繁體中文',    flag: '🇹🇼' },
        { code: 'th',    label: 'ภาษาไทย',    flag: '🇹🇭' },
        { code: 'hi',    label: 'हिंदी',      flag: '🇮🇳' },
        { code: 'ne',    label: 'नेपाली',     flag: '🇳🇵' },
        { code: 'bn',    label: 'বাংলা',      flag: '🇧🇩' },
      ],
    },
  ];

  const languages = languageGroups.flatMap(g => g.langs);
  const currentLangObj = languages.find(l => l.code === currentLang) || languages[0];
  const userName = userNickname || user?.displayName || user?.email?.split('@')[0] || 'Explorer';

  const navGroups = [
    {
      key: 'study',
      label: 'Study',
      items: [
        { label: 'Vocabulary Lists', icon: '📖', action: () => { routerNav('/'); onNavigateToModule && onNavigateToModule('vocab'); } },
        { label: 'Kanji Master',     icon: '🈶', action: () => { routerNav('/'); onNavigateToModule && onNavigateToModule('kanji'); } },
        { label: 'Grammar Lab',      icon: '✏️', action: () => { routerNav('/'); onNavigateToModule && onNavigateToModule('grammar'); } },
        { label: 'Reading Practice', icon: '📰', action: () => { routerNav('/'); onNavigateToModule && onNavigateToModule('reading'); } },
      ],
    },
    {
      key: 'practice',
      label: 'Practice',
      items: [
        { label: 'JLPT Quizzes', icon: '🎯', action: () => { routerNav('/'); onNavigateToModule && onNavigateToModule('quiz'); } },
        { label: 'Drill Test',   icon: '⚡', action: () => { routerNav('/'); onNavigateToModule && onNavigateToModule('drill'); } },
      ],
    },
    {
      key: 'extras',
      label: 'Extras',
      items: [
        { label: 'Verb Pairs',            icon: '🔁', action: () => routerNav('/verb-pairs') },
        { label: 'Sounds (Onomatopoeia)', icon: '🎵', action: () => routerNav('/onomatopoeia') },
      ],
    },
    {
      key: 'explore',
      label: 'Explore',
      items: [
        { label: 'Japan Explorer', icon: '🗾', action: () => routerNav('/japan-explorer') },
        { label: 'Japan Events',   icon: '🎌', action: () => routerNav('/japan-events') },
        { label: 'Blog',           icon: '📝', action: () => routerNav('/blog') },
      ],
    },
    {
      key: 'info',
      label: 'About',
      items: [
        { label: 'Our Story',  icon: '🌸', action: () => routerNav('/our-story') },
        { label: 'Contact Us', icon: '✉️', action: () => routerNav('/contact') },
      ],
    },
  ];

  const handleNavItem = (action) => {
    setActiveNavMenu(null);
    setDrawerOpen(false);
    action();
  };

  return (
    <>
      <nav className="sticky top-0 z-50 w-full border-b border-slate-200/60 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md transition-colors duration-300">
        <div className="max-w-[1600px] mx-auto px-4 md:px-6 h-14 md:h-20 flex justify-between items-center">

          {/* Brand Logo */}
          <Logo
            size="md"
            onClick={() => { routerNav('/'); onNavigate('dashboard'); }}
          />

          {/* Desktop centre nav — hidden in study mode to avoid double header */}
          <nav className="hidden md:flex items-center gap-1" ref={navRef} style={{ display: currentView === 'practice' ? 'none' : undefined }}>
            {navGroups.map((group) => (
              <div key={group.key} className="relative">
                <button
                  onClick={() => setActiveNavMenu(activeNavMenu === group.key ? null : group.key)}
                  className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold transition-all ${
                    activeNavMenu === group.key
                      ? 'bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400'
                      : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'
                  }`}
                >
                  {group.label}
                  <ChevronDown size={12} className={`transition-transform duration-200 ${activeNavMenu === group.key ? 'rotate-180' : ''}`} />
                </button>
                {activeNavMenu === group.key && (
                  <div className="absolute left-0 top-[calc(100%+8px)] w-52 bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-100 dark:border-slate-700 overflow-hidden animate-in fade-in slide-in-from-top-2 z-50">
                    {group.items.map((item) => (
                      <button
                        key={item.label}
                        onClick={() => handleNavItem(item.action)}
                        className="w-full text-left px-4 py-3 text-sm font-bold flex items-center gap-3 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-rose-600 dark:hover:text-rose-400 transition-colors"
                      >
                        <span className="text-base">{item.icon}</span> {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {/* Pricing — direct link */}
            <button
              onClick={() => { setActiveNavMenu(null); routerNav('/study-plans'); }}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white transition-all"
            >
              💰 Pricing
            </button>
            {/* Shop — direct link */}
            <button
              onClick={() => { setActiveNavMenu(null); routerNav('/shop'); }}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white transition-all"
            >
              🛍️ Shop
            </button>
          </nav>

          {/* Desktop actions — hidden on mobile */}
          <div className="hidden md:flex items-center gap-3 md:gap-6">

            <button
              onClick={onOpenSettings}
              className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-600 dark:text-slate-300 hover:scale-105 hover:bg-slate-200 dark:hover:bg-slate-700 transition-all shadow-sm border border-slate-200/50 dark:border-slate-700"
              title="Settings"
              aria-label="Open settings"
            >
              <Settings size={20} />
            </button>

            {onThemeChange && currentTheme ? (
              <ThemeSwitcher
                currentTheme={currentTheme}
                onThemeChange={onThemeChange}
                compact={true}
                darkNav={isDarkMode}
              />
            ) : (
              <button
                onClick={onToggleDark}
                className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-lg hover:scale-105 transition-all shadow-sm border border-slate-200/50 dark:border-slate-700"
                title="Toggle Dark Mode"
                aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDarkMode ? '🌙' : '☀️'}
              </button>
            )}

            {streak > 0 && (
              <div
                className="flex items-center gap-1.5 bg-orange-50 dark:bg-orange-500/10 px-3 py-1.5 rounded-full border border-orange-200 dark:border-orange-500/20 shadow-sm cursor-help transition-transform hover:scale-105"
                title={`${streak} Day Learning Streak!`}
              >
                <span className="text-orange-500 text-lg animate-pulse">🔥</span>
                <span className="font-black text-orange-700 dark:text-orange-400 text-sm">{streak}</span>
              </div>
            )}

            {/* Language picker — desktop */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => { setShowLangMenu(!showLangMenu); setShowProfileMenu(false); }}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors text-xs font-bold text-slate-700 dark:text-slate-300 border border-transparent"
              >
                <span className="text-base">{currentLangObj.flag}</span>
                <span>{currentLangObj.label}</span>
                <span className="text-[10px] text-slate-400">▼</span>
              </button>
              {showLangMenu && (
                <div className="absolute right-0 mt-2 w-52 max-w-[calc(100vw-32px)] bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-100 dark:border-slate-700 overflow-hidden animate-in fade-in slide-in-from-top-2 max-h-80 overflow-y-auto">
                  {languageGroups.map((group, gi) => (
                    <div key={group.label}>
                      {gi > 0 && <div className="border-t border-slate-100 dark:border-slate-700" />}
                      <p className="px-4 pt-2.5 pb-1 text-[9px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">{group.label}</p>
                      {group.langs.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => { onLangChange(lang.code); setShowLangMenu(false); }}
                          className={`w-full text-left px-4 py-2.5 text-sm font-bold flex items-center gap-3 transition-colors ${currentLang === lang.code ? 'bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'}`}
                        >
                          <span className="text-lg">{lang.flag}</span> {lang.label}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Profile / Auth — desktop */}
            <div className="relative flex items-center pl-6 border-l border-slate-200 dark:border-slate-700" ref={profileRef}>
              {!user ? (
                <div className="flex items-center gap-2">
                  <button
                    onClick={onLogin}
                    className="text-xs font-black text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors px-3 py-2"
                  >
                    Log in
                  </button>
                  <button
                    onClick={onSignUp}
                    className="text-xs font-black bg-rose-600 text-white px-4 py-2 rounded-full hover:bg-rose-700 transition-colors shadow-sm"
                  >
                    Sign up free →
                  </button>
                </div>
              ) : (
              <>
              <button
                onClick={() => { setShowProfileMenu(!showProfileMenu); setShowLangMenu(false); }}
                className="flex items-center gap-3 text-left group"
              >
                <div className="text-right">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 group-hover:text-rose-500 transition-colors">Welcome back</p>
                  <p className="text-xs font-bold text-slate-900 dark:text-white truncate max-w-[120px]">{userName}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center group-hover:bg-rose-50 dark:group-hover:bg-rose-500/10 group-hover:text-rose-600 transition-all shadow-sm border border-slate-200/50 dark:border-slate-700">
                  <span className="text-lg group-hover:rotate-12 transition-transform">🚀</span>
                </div>
              </button>
              {showProfileMenu && (
                <div className="absolute right-0 top-[120%] mt-2 w-56 max-w-[calc(100vw-32px)] bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-100 dark:border-slate-700 overflow-hidden animate-in fade-in slide-in-from-top-2">
                  <div className="px-5 py-4 border-b border-slate-50 dark:border-slate-700/50 bg-slate-50/50 dark:bg-slate-800/50">
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-1">Signed in as</p>
                    <p className="text-sm font-bold text-slate-700 dark:text-slate-300 truncate">{user?.email}</p>
                  </div>
                  <div className="p-2">
                    <button
                      onClick={() => { setShowProfileMenu(false); routerNav('/'); onNavigate('dashboard'); }}
                      className="w-full text-left px-4 py-2.5 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors flex items-center gap-3 mb-1"
                    >
                      <span className="text-base opacity-70">🏠</span> Dashboard
                    </button>
                    <button
                      onClick={() => { setShowProfileMenu(false); onSignOut(); }}
                      className="w-full text-left px-4 py-2.5 text-xs font-bold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-500/10 rounded-xl transition-colors flex items-center gap-3"
                    >
                      <span className="text-base opacity-70">🚪</span> Sign Out
                    </button>
                  </div>
                </div>
              )}
              </>
              )}
            </div>
          </div>

          {/* Mobile: streak badge + hamburger */}
          <div className="flex md:hidden items-center gap-2">
            {streak > 0 && (
              <div className="flex items-center gap-1 bg-orange-50 dark:bg-orange-500/10 px-2 py-1 rounded-full border border-orange-200 dark:border-orange-500/20">
                <span className="text-orange-500 text-sm">🔥</span>
                <span className="font-black text-orange-700 dark:text-orange-400 text-xs">{streak}</span>
              </div>
            )}
            <button
              onClick={() => setDrawerOpen(true)}
              className="w-9 h-9 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-600 dark:text-slate-300 border border-slate-200/50 dark:border-slate-700"
              aria-label="Open menu"
            >
              <Menu size={20} />
            </button>
          </div>

        </div>
      </nav>

      {/* Mobile Drawer Overlay */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm md:hidden"
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile Slide-in Drawer */}
      <div ref={drawerRef} className={`fixed top-0 right-0 bottom-0 z-[70] w-72 max-w-[85vw] bg-white dark:bg-slate-900 shadow-2xl flex flex-col transition-transform duration-300 md:hidden ${drawerOpen ? 'translate-x-0' : 'translate-x-full'}`}>

        {/* Drawer header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-800">
          <Logo size="sm" />
          <button
            onClick={() => setDrawerOpen(false)}
            className="w-8 h-8 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 dark:text-slate-400"
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>

        {/* User info */}
        <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30">
          {user ? (
            <>
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Signed in as</p>
              <p className="text-sm font-bold text-slate-800 dark:text-white truncate">{userName}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </>
          ) : (
            <>
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Exploring as guest</p>
              <p className="text-sm font-bold text-slate-800 dark:text-white truncate">Sign up to save progress</p>
              <p className="text-xs text-slate-400 truncate">N5 is free · N4–N1 need Premium</p>
            </>
          )}
        </div>

        {/* Drawer items */}
        <div className="flex-1 overflow-y-auto py-3 px-3">

          {/* Dark mode toggle */}
          <div className="flex items-center justify-between px-3 py-3 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
            <div className="flex items-center gap-3">
              <span className="text-xl">{isDarkMode ? '🌙' : '☀️'}</span>
              <span className="text-sm font-bold text-slate-700 dark:text-slate-200">Dark Mode</span>
            </div>
            <button
              onClick={onToggleDark}
              className={`relative w-12 h-6 rounded-full transition-colors ${isDarkMode ? 'bg-rose-500' : 'bg-slate-200 dark:bg-slate-700'}`}
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              role="switch"
              aria-checked={isDarkMode}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${isDarkMode ? 'translate-x-6' : 'translate-x-0'}`} />
            </button>
          </div>

          {/* Language picker */}
          <div className="mt-1">
            <button
              onClick={() => setShowLangMenu(!showLangMenu)}
              className="w-full flex items-center justify-between px-3 py-3 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-xl">{currentLangObj.flag}</span>
                <span className="text-sm font-bold text-slate-700 dark:text-slate-200">Language</span>
              </div>
              <span className="text-xs text-slate-400 font-bold">{currentLangObj.label} ▼</span>
            </button>
            {showLangMenu && (
              <div className="mx-2 mt-1 bg-slate-50 dark:bg-slate-800 rounded-xl overflow-hidden border border-slate-100 dark:border-slate-700 max-h-60 overflow-y-auto">
                {languageGroups.map((group, gi) => (
                  <div key={group.label}>
                    {gi > 0 && <div className="border-t border-slate-200 dark:border-slate-700" />}
                    <p className="px-4 pt-2 pb-0.5 text-[9px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">{group.label}</p>
                    {group.langs.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => { onLangChange(lang.code); setShowLangMenu(false); setDrawerOpen(false); }}
                        className={`w-full text-left px-4 py-2.5 text-sm font-bold flex items-center gap-3 transition-colors ${currentLang === lang.code ? 'bg-rose-50 dark:bg-rose-500/20 text-rose-600' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                      >
                        <span>{lang.flag}</span> {lang.label}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Streak */}
          {streak > 0 && (
            <div className="flex items-center gap-3 px-3 py-3 rounded-xl mt-1">
              <span className="text-xl">🔥</span>
              <span className="text-sm font-bold text-slate-700 dark:text-slate-200">Streak</span>
              <span className="ml-auto font-black text-orange-600 dark:text-orange-400 text-sm">{streak} days</span>
            </div>
          )}

          {/* Settings */}
          <button
            onClick={() => { setDrawerOpen(false); onOpenSettings(); }}
            className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors mt-1"
          >
            <Settings size={20} className="text-slate-500 dark:text-slate-400" />
            <span className="text-sm font-bold text-slate-700 dark:text-slate-200">Settings</span>
          </button>

          {/* Dashboard */}
          <button
            onClick={() => { setDrawerOpen(false); routerNav('/'); onNavigate('dashboard'); }}
            className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors mt-1"
          >
            <span className="text-xl">🏠</span>
            <span className="text-sm font-bold text-slate-700 dark:text-slate-200">Dashboard</span>
          </button>

          {/* Pricing */}
          <div className="mt-3">
            <p className="px-3 pb-1 text-[9px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Pricing</p>
            <button
              onClick={() => handleNavItem(() => routerNav('/study-plans'))}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-slate-600 dark:text-slate-300 hover:text-rose-600 dark:hover:text-rose-400"
            >
              <span className="text-base">💰</span>
              <span className="text-sm font-bold">Plans & Pricing</span>
            </button>
          </div>

          {/* Shop */}
          <div className="mt-3">
            <p className="px-3 pb-1 text-[9px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Shop</p>
            <button
              onClick={() => handleNavItem(() => routerNav('/shop'))}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-slate-600 dark:text-slate-300 hover:text-rose-600 dark:hover:text-rose-400"
            >
              <span className="text-base">🛍️</span>
              <span className="text-sm font-bold">Shop</span>
            </button>
          </div>

          {/* Nav groups in drawer */}
          {navGroups.map((group) => (
            <div key={group.key} className="mt-3">
              <p className="px-3 pb-1 text-[9px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">{group.label}</p>
              {group.items.map((item) => (
                <button
                  key={item.label}
                  onClick={() => handleNavItem(item.action)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-slate-600 dark:text-slate-300 hover:text-rose-600 dark:hover:text-rose-400"
                >
                  <span className="text-base">{item.icon}</span>
                  <span className="text-sm font-bold">{item.label}</span>
                </button>
              ))}
            </div>
          ))}
        </div>

        {/* Sign out / Auth */}
        <div className="px-3 py-4 border-t border-slate-100 dark:border-slate-800">
          {!user ? (
            <div className="flex flex-col gap-2">
              <button
                onClick={() => { setDrawerOpen(false); onSignUp && onSignUp(); }}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-rose-600 text-white font-black text-sm hover:bg-rose-700 transition-colors shadow-sm"
              >
                Sign up free →
              </button>
              <button
                onClick={() => { setDrawerOpen(false); onLogin && onLogin(); }}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-black text-sm hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
              >
                Log in
              </button>
            </div>
          ) : (
            <button
              onClick={() => { setDrawerOpen(false); onSignOut(); }}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 font-black text-sm hover:bg-rose-100 dark:hover:bg-rose-500/20 transition-colors"
            >
              <span>🚪</span> Sign Out
            </button>
          )}
        </div>
      </div>
    </>
  );
}
